import { Card, Classes, Dialog, DialogBody, DialogFooter, FormGroup, Icon, InputGroup } from "@blueprintjs/core";
import classNames from "classnames";
import React, { ChangeEvent, useState } from "react";

import { useGetSport } from "../../redux/slices";
import { FrameStencil, SportPlay } from "../../types";
import { getPlayingSurfaceLabelFromSport } from "../../utils";
import { Button } from "../Button/button";
import { TagCreator } from "../TagCreator/tag-creator";
import { MultiSelect } from "../multi-select";
import { PlayingSurface } from "../playing-surface/playing-surface";
import { Select } from "../select";
import { Tag } from "../tag/tag";
import styles from "./edit-play.module.scss";
import {
  UseEditPlay,
  useSelectPlayingSurface,
  useSelectSeason,
  useSelectSeries,
  useSelectTags,
  useSelectTeam,
} from "./hooks";

const EMPTY_STENCILS: FrameStencil[] = [];

/** Wrapper Component to check editingPlay value */
export const EditPlay = <TPlay extends SportPlay>(props: UseEditPlay<TPlay>) => {
  if (!props.editingPlay) return null;
  return <EditPlayContents {...props} editingPlay={props.editingPlay} />;
};

export interface EditPlayProps<TPlay extends SportPlay> extends Omit<UseEditPlay<TPlay>, "editingPlay"> {
  editingPlay: TPlay;
}

export const EditPlayContents = <TPlay extends SportPlay>({
  editingPlay,
  setEditingPlay,
  updatePlay,
  closeEditPlay,
  errors,
  savePlay,
}: EditPlayProps<TPlay>) => {
  /** Tag Creator */
  const [tagCreatorOpen, setTagCreatorOpen] = useState(false);
  const showTagCreator = () => {
    setTagCreatorOpen(true);
  };
  const hideTagCreator = () => {
    setTagCreatorOpen(false);
  };

  /** Label */
  const sport = useGetSport();
  const playingSurfaceLabel = getPlayingSurfaceLabelFromSport(sport);

  /** form handlers */
  const handleChangePlayName = (event: ChangeEvent<HTMLInputElement>) => {
    updatePlay((prevValue) => ({ ...prevValue, name: event.target.value }));
  };
  const handleChangePlayNotes = (event: ChangeEvent<HTMLInputElement>) => {
    updatePlay((prevValue) => ({ ...prevValue, notes: event.target.value }));
  };

  const { seriesSelect, handleNewSeries } = useSelectSeries({ editingPlay, updatePlay });
  const { teamSelect, handleNewTeam } = useSelectTeam({ editingPlay, updatePlay });
  const { tagSelect } = useSelectTags({ editingPlay, updatePlay });
  const { playingSurfaceSelect } = useSelectPlayingSurface({ editingPlay, updatePlay });
  const { seasonSelect } = useSelectSeason({ editingPlay, updatePlay });

  return (
    <Dialog isOpen title="Play Details" className={styles.playDetailsContainer} onClose={closeEditPlay}>
      <DialogBody className={styles.dialogBody}>
        <Card className={classNames(styles.formContainer, [Classes.ELEVATION_1])}>
          <FormGroup
            label="Play Name"
            labelInfo="*"
            inline
            intent={errors.name ? "danger" : "none"}
            helperText={errors.name}
          >
            <InputGroup placeholder="Play Name" fill value={editingPlay.name || ""} onChange={handleChangePlayName} />
          </FormGroup>

          <FormGroup label="Team" inline>
            <Select {...teamSelect} fill placeholder="Select Team" createNewItemFromQuery={handleNewTeam} />
          </FormGroup>

          <FormGroup label="Series" inline>
            <Select
              {...seriesSelect}
              fill
              placeholder="Select Series"
              noResultsText="No Series. Start typing to create a new Series."
              createNewItemFromQuery={handleNewSeries}
            />
          </FormGroup>

          <FormGroup label="Tags" inline className={styles.tagGroup}>
            <MultiSelect
              {...tagSelect}
              fill
              placeholder="Select Tags"
              tagRenderer={(tag) => (
                <div data-color={tag.color} data-text-color={tag.textColor}>
                  {tag.name}
                </div>
              )}
              tagInputProps={{
                tagProps: (value: any) => {
                  const color: string | undefined = value?.props?.["data-color"];
                  const textColor: string | undefined = value?.props?.["data-text-color"];
                  return { style: { backgroundColor: color, color: textColor } };
                },
              }}
              itemRenderer={(item) => <Tag tag={item} />}
            />
            <Icon icon="add" onClick={showTagCreator} className={styles.tagCreator} />
          </FormGroup>

          <FormGroup label={`${playingSurfaceLabel} Type`} inline>
            <Select {...playingSurfaceSelect} fill placeholder={`Select ${playingSurfaceLabel}`} />
          </FormGroup>

          <FormGroup label={"Season"} inline>
            <Select {...seasonSelect} fill placeholder="Select Season" />
          </FormGroup>

          <FormGroup label="Play Notes" inline>
            <InputGroup
              placeholder="Play Notes"
              fill
              value={editingPlay.notes || ""}
              onChange={handleChangePlayNotes}
            />
          </FormGroup>
        </Card>
        <div className={styles.previewContainer}>{<PlayingSurface play={editingPlay} stencils={EMPTY_STENCILS} />}</div>
        <TagCreator show={tagCreatorOpen} handleClose={hideTagCreator} updatePlay={updatePlay} />
      </DialogBody>
      <DialogFooter
        actions={
          <>
            <Button minimal onClick={() => setEditingPlay(undefined)}>
              Cancel
            </Button>
            <Button intent="primary" onClick={savePlay}>
              Save
            </Button>
          </>
        }
      />
    </Dialog>
  );
};
